import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { compressImage } from "../lib/photoUpload";
import { validateImageFile } from "../lib/fileValidation";
import { PHOTO_UPLOAD_URL, PM4SUBS_API_URL } from "../lib/apiConfig";
import * as api from "../lib/supabase";
import { supabase } from "../lib/supabase";

// Photo categories
const CATEGORIES = [
  { value: "progress", label: "Progress" },
  { value: "safety", label: "Safety" },
  { value: "quality", label: "Quality" },
  { value: "issue", label: "Issue/Problem" },
  { value: "completed", label: "Completed Work" },
  { value: "material", label: "Material/Delivery" },
  { value: "drive_by", label: "Drive-By" },
  { value: "other", label: "Other" },
];

const colors = {
  primary: "#1a5a8e",
  white: "#ffffff",
  gray100: "#f1f5f9",
  gray200: "#e2e8f0",
  gray300: "#cbd5e1",
  gray400: "#94a3b8",
  gray500: "#64748b",
  gray600: "#475569",
  gray700: "#334155",
  gray800: "#1e293b",
  success: "#10b981",
  warning: "#f59e0b",
};

export default function QuickPhoto({ currentUser, orgId, onBack }) {
  const [step, setStep] = useState("capture"); // capture, selectJob, uploading, done
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [allJobs, setAllJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [category, setCategory] = useState("drive_by");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Load ALL jobs (including inactive) on mount
  useEffect(() => {
    loadAllJobs();
  }, [orgId]);

  const loadAllJobs = async () => {
    setLoadingJobs(true);
    try {
      // Get ALL jobs, not just active ones
      const { data, error } = await supabase
        .from("t4s_jobs")
        .select(
          `
          id,
          job_number,
          name,
          address,
          city,
          state,
          status,
          pm4subs_project_id
        `,
        )
        .eq("org_id", orgId)
        .order("job_number", { ascending: false });

      if (error) throw error;
      setAllJobs(data || []);
    } catch (err) {
      console.error("Error loading jobs:", err);
      setError("Failed to load jobs");
    } finally {
      setLoadingJobs(false);
    }
  };

  // Filter jobs by search query
  const filteredJobs = allJobs.filter((job) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      job.job_number?.toLowerCase().includes(q) ||
      job.name?.toLowerCase().includes(q) ||
      job.address?.toLowerCase().includes(q) ||
      job.city?.toLowerCase().includes(q)
    );
  });

  // Open camera directly
  const openCamera = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle photo capture
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // Validate file type and size before processing
      const validation = await validateImageFile(file, {
        maxSize: 20 * 1024 * 1024,
      });
      if (!validation.valid) {
        setError(validation.error);
        return;
      }

      // Compress the image
      const compressed = await compressImage(file, 1920, 0.8);

      setPhotoFile(compressed);
      setPhotoPreview(URL.createObjectURL(compressed));
      setStep("selectJob");
    } catch (err) {
      console.error("Error processing photo:", err);
      setError("Failed to process photo");
    }
  };

  // Upload photo to selected job
  const uploadPhoto = async () => {
    if (!photoFile || !selectedJob) return;

    setUploading(true);
    setError(null);

    try {
      // Get current location
      let gps = null;
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 5000,
          });
        });
        gps = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
      } catch (e) {
        // GPS unavailable
      }

      const today = new Date().toISOString().split("T")[0];
      const timestamp = Date.now();

      // Upload to R2 via Cloudflare Worker (same as other photos)
      const key = `job-photos/${orgId}/${selectedJob.id}/${today}/${timestamp}.jpg`;

      const uploadResponse = await fetch(
        `${PHOTO_UPLOAD_URL}?key=${encodeURIComponent(key)}`,
        {
          method: "PUT",
          body: photoFile,
          headers: { "Content-Type": "image/jpeg" },
        },
      );

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({}));
        throw new Error(errorData.error || "Upload failed");
      }

      // Save to database
      await api.saveJobPhoto({
        orgId,
        jobId: selectedJob.id,
        photoUrl: key,
        category,
        description: notes || `Drive-by photo of ${selectedJob.job_number}`,
        latitude: gps?.latitude,
        longitude: gps?.longitude,
        takenBy: currentUser?.id,
      });

      // If job has PM4Subs project, also upload there
      if (selectedJob.pm4subs_project_id) {
        try {
          const formData = new FormData();
          formData.append("file", photoFile);
          formData.append("project_id", selectedJob.pm4subs_project_id);
          formData.append("category", category);
          formData.append("notes", notes || `Drive-by photo`);
          if (gps) {
            formData.append("latitude", gps.latitude);
            formData.append("longitude", gps.longitude);
          }

          await fetch(`${PM4SUBS_API_URL}/api/photos/upload`, {
            method: "POST",
            body: formData,
          });
        } catch (e) {
          console.error("Could not sync photo to PM4Subs:", e);
          toast.warning("Photo saved, but PM4Subs sync failed");
        }
      }

      setStep("done");
    } catch (err) {
      console.error("Error uploading photo:", err);
      setError("Failed to upload photo: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  // Reset for another photo
  const takeAnother = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
    setSelectedJob(null);
    setNotes("");
    setCategory("drive_by");
    setStep("capture");
  };

  return (
    <div style={{ minHeight: "100vh", background: colors.gray100 }}>
      {/* Header */}
      <div
        style={{
          padding: "16px",
          background: colors.primary,
          color: colors.white,
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "rgba(255,255,255,0.2)",
            border: "none",
            borderRadius: 8,
            padding: "8px 12px",
            color: colors.white,
            fontSize: 16,
            cursor: "pointer",
          }}
        >
          ←
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
            Quick Photo
          </h1>
          <p style={{ margin: 0, fontSize: 12, opacity: 0.8 }}>
            Snap a photo of any job
          </p>
        </div>
      </div>

      {/* Hidden file input for camera */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      {/* Error display */}
      {error && (
        <div
          style={{
            margin: 16,
            padding: 12,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 8,
            color: "#dc2626",
            fontSize: 14,
          }}
        >
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              float: "right",
              background: "none",
              border: "none",
              color: "#dc2626",
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Step 1: Capture */}
      {step === "capture" && (
        <div style={{ padding: 16, textAlign: "center" }}>
          <div
            style={{
              background: colors.white,
              borderRadius: 16,
              padding: 40,
              marginBottom: 16,
            }}
          >
            <div style={{ fontSize: 64, marginBottom: 16 }}>📷</div>
            <h2 style={{ margin: "0 0 8px", color: colors.gray800 }}>
              Take a Job Photo
            </h2>
            <p style={{ margin: "0 0 24px", color: colors.gray500 }}>
              Snap a quick photo while driving by any job site
            </p>
            <button
              onClick={openCamera}
              style={{
                width: "100%",
                padding: "16px 24px",
                background: colors.primary,
                color: colors.white,
                border: "none",
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Open Camera
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Select Job */}
      {step === "selectJob" && (
        <div style={{ padding: 16 }}>
          {/* Photo Preview */}
          <div
            style={{
              background: colors.white,
              borderRadius: 12,
              overflow: "hidden",
              marginBottom: 16,
            }}
          >
            <img
              src={photoPreview}
              alt="Captured"
              style={{
                width: "100%",
                height: 200,
                objectFit: "cover",
              }}
            />
            <div style={{ padding: 12 }}>
              <button
                onClick={takeAnother}
                style={{
                  padding: "8px 16px",
                  background: colors.gray200,
                  border: "none",
                  borderRadius: 8,
                  color: colors.gray700,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Retake Photo
              </button>
            </div>
          </div>

          {/* Job Selection */}
          <div
            style={{
              background: colors.white,
              borderRadius: 12,
              padding: 16,
              marginBottom: 16,
            }}
          >
            <h3
              style={{
                margin: "0 0 12px",
                fontSize: 15,
                fontWeight: 600,
                color: colors.gray800,
              }}
            >
              Which job is this?
            </h3>

            {/* Search */}
            <input
              type="text"
              placeholder="Search by job #, name, or address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "12px 14px",
                border: `1px solid ${colors.gray300}`,
                borderRadius: 8,
                fontSize: 14,
                marginBottom: 12,
                boxSizing: "border-box",
              }}
            />

            {/* Job List */}
            <div
              style={{
                maxHeight: 250,
                overflowY: "auto",
                border: `1px solid ${colors.gray200}`,
                borderRadius: 8,
              }}
            >
              {loadingJobs ? (
                <div
                  style={{
                    padding: 20,
                    textAlign: "center",
                    color: colors.gray500,
                  }}
                >
                  Loading jobs...
                </div>
              ) : filteredJobs.length === 0 ? (
                <div
                  style={{
                    padding: 20,
                    textAlign: "center",
                    color: colors.gray500,
                  }}
                >
                  No jobs found
                </div>
              ) : (
                filteredJobs.map((job) => (
                  <button
                    key={job.id}
                    onClick={() => setSelectedJob(job)}
                    style={{
                      width: "100%",
                      padding: "12px 14px",
                      background:
                        selectedJob?.id === job.id
                          ? colors.primary + "15"
                          : "transparent",
                      border: "none",
                      borderBottom: `1px solid ${colors.gray100}`,
                      textAlign: "left",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background:
                          job.status === "active"
                            ? colors.success
                            : colors.gray400,
                        flexShrink: 0,
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: colors.gray800,
                        }}
                      >
                        #{job.job_number} - {job.name}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: colors.gray500,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {job.address}, {job.city}
                        {job.status !== "active" && (
                          <span
                            style={{
                              marginLeft: 8,
                              padding: "2px 6px",
                              background: colors.gray200,
                              borderRadius: 4,
                              fontSize: 10,
                              textTransform: "uppercase",
                            }}
                          >
                            {job.status}
                          </span>
                        )}
                      </div>
                    </div>
                    {selectedJob?.id === job.id && (
                      <span style={{ color: colors.primary, fontSize: 18 }}>
                        ✓
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Category & Notes (only show if job selected) */}
          {selectedJob && (
            <div
              style={{
                background: colors.white,
                borderRadius: 12,
                padding: 16,
                marginBottom: 16,
              }}
            >
              {/* Category */}
              <div style={{ marginBottom: 12 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: colors.gray600,
                    marginBottom: 6,
                  }}
                >
                  Category
                </label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => setCategory(cat.value)}
                      style={{
                        padding: "6px 12px",
                        borderRadius: 16,
                        border:
                          category === cat.value
                            ? `2px solid ${colors.primary}`
                            : `1px solid ${colors.gray300}`,
                        background:
                          category === cat.value
                            ? colors.primary
                            : "transparent",
                        color:
                          category === cat.value
                            ? colors.white
                            : colors.gray600,
                        fontSize: 12,
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: colors.gray600,
                    marginBottom: 6,
                  }}
                >
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add a note about this photo..."
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `1px solid ${colors.gray300}`,
                    borderRadius: 8,
                    fontSize: 14,
                    resize: "vertical",
                    minHeight: 60,
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>
          )}

          {/* Upload Button */}
          <button
            onClick={uploadPhoto}
            disabled={!selectedJob || uploading}
            style={{
              width: "100%",
              padding: "16px 24px",
              background: selectedJob ? colors.primary : colors.gray300,
              color: selectedJob ? colors.white : colors.gray500,
              border: "none",
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 600,
              cursor: selectedJob ? "pointer" : "not-allowed",
            }}
          >
            {uploading
              ? "Uploading..."
              : selectedJob
                ? `Save to #${selectedJob.job_number}`
                : "Select a job first"}
          </button>
        </div>
      )}

      {/* Step 3: Done */}
      {step === "done" && (
        <div style={{ padding: 16, textAlign: "center" }}>
          <div
            style={{
              background: colors.white,
              borderRadius: 16,
              padding: 40,
            }}
          >
            <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
            <h2 style={{ margin: "0 0 8px", color: colors.gray800 }}>
              Photo Saved!
            </h2>
            <p style={{ margin: "0 0 24px", color: colors.gray500 }}>
              Added to #{selectedJob?.job_number} - {selectedJob?.name}
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={takeAnother}
                style={{
                  flex: 1,
                  padding: "14px 24px",
                  background: colors.primary,
                  color: colors.white,
                  border: "none",
                  borderRadius: 12,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Take Another
              </button>
              <button
                onClick={onBack}
                style={{
                  flex: 1,
                  padding: "14px 24px",
                  background: colors.gray200,
                  color: colors.gray700,
                  border: "none",
                  borderRadius: 12,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
